import { z } from 'zod';
import { TimestampSchema } from '../types/common.types';

/**
 * Bejövő igény-katalógus — amit egy KÜLSŐ front (elsőként az rPI Web) **beküldhet**
 * az rPI ERP-be.
 *
 * ADR-145 (`D` üzleti modell, Javo-ratify 2026-09-08) és a `#1910` esemény-katalógus
 * párja: az `integration-events.schema.ts` a **kifelé** publikált alak, ez a **befelé**
 * fogadott. A kettő szándékosan NEM szimmetrikus — lásd lent.
 *
 * ## A vezérelv: a web IGÉNYT rögzít, az ERP ELSZÁMOL
 *
 * Ezért itt **nincs** ár, kedvezmény, kaució, készlet-foglalás vagy állapot: azokat az
 * ERP számolja. A beküldő azt mondja meg, hogy **mit szeretnének**, nem azt, hogy
 * mennyibe kerül vagy hogy elfogadható-e. Egy `booking.requested` NEM foglalás — a
 * jóváhagyott bérlés az ERP oldalán keletkezik, és `rental.created`-ként megy vissza.
 *
 * ## Névkonvenció — `<fogalom>.<múlt idejű ige>`, snake_case
 *
 * Ugyanaz, mint a kimenő katalógusban. ⛔ De az igék KÜLÖNBÖZNEK: kifelé
 * `created`/`changed` (megtörtént tény), befelé `requested`/`submitted` (kérés).
 * Ha ugyanazt a szót használnánk, a fogadó oldal a kérést ténynek olvasná.
 *
 * ## Az `5` fogalom
 *
 * A halmaz az rPI Web MÉRT igény-tábláiból jön (🅾️ Omega, 2026-09-08:
 * `Contact` · `ServiceRequest` · `PartsLead` · `Booking` · `ShopOrder`), nem tervezésből.
 *
 * ## Idempotencia — a beküldő oldal felelőssége is
 *
 * A hálózat mindkét irányban at-least-once. A beküldő `requestId`-t ad; az ERP
 * ugyanazt a `requestId`-t másodszor látva NEM hoz létre új tételt.
 *
 * ## Külső azonosító — enélkül nincs visszacsatolás
 *
 * Az `externalId` a beküldő SAJÁT kulcsa a saját során. Ez teszi lehetővé, hogy a
 * válasz-esemény (`rental.created`) visszavezethető legyen a web sorára — az ERP-oldali
 * `uuid` és a web-oldali üzleti kulcs (mérve: `"ABV 1"`, `"s01"`, `"BK-<ms>"`) ugyanis
 * NEM azonos névtér.
 *
 * @see ADR-145 · `integration-events.schema.ts` (a kimenő pár)
 */

/** A bejövő katalógus verziója. Additív mezőnél minor, törő változásnál major. */
export const INBOUND_REQUEST_SCHEMA_VERSION = '1.1' as const;
/**
 * A fogadott sema-verziok. ⛔ `E7`: ismeretlen NEV **vagy** ismeretlen VERZIO -> `4xx`.
 *
 * ⚠️ Az `1.0` NINCS benne, es ez szandekos: a `booking.requested` `productId` mezoje ott
 * merve rossz tablara mutatott (l. lent) — egy `1.0`-s beküldés befogadása azt jelentene,
 * hogy a hibas alakot tovabb szolgaljuk ki. Merve `2026-09-10`: `integration_keys` `0` sor
 * => `0` elo kuldo, tehat ma nincs kit kizarni ezzel.
 */
export const ACCEPTED_INBOUND_SCHEMA_VERSIONS = ['1.1'] as const;

/** A fogadott igény-nevek zárt készlete. */
export const InboundRequestNameSchema = z.enum([
  'contact.submitted',
  'service.requested',
  'parts.inquiry_submitted',
  'booking.requested',
  'order.placed',
]);
export type InboundRequestName = z.infer<typeof InboundRequestNameSchema>;

/**
 * A beküldő természetes személy-kulcsa.
 *
 * 🔑 Az rPI Weben MA **nincs végfelhasználói fiók** (🅾️ mérése, 2026-09-08: a foglalás
 * `foglalas/[token]` linkkel megy) ⇒ a személyt **e-mail és/vagy telefon** azonosítja,
 * nem `userId`. Ezért itt legalább az egyik KÖTELEZŐ.
 *
 * ⚠️ A normalizálás a BEKÜLDŐ dolga (kisbetűs, trimmelt e-mail; E.164 telefon) — az ERP
 * ezen a kulcson próbál meglévő `Partner`-hez kötni. Normalizálatlan bemenet ugyanazt a
 * személyt kétszer hozza be, és az összevonás utólag drága.
 */
export const InboundCustomerSchema = z
  .object({
    email: z.string().email().max(255).optional(),
    /** E.164 alak, pl. `+36301234567`. */
    phone: z
      .string()
      .regex(/^\+[1-9]\d{6,14}$/, 'E.164 alak kötelező (pl. +36301234567)')
      .optional(),
    name: z.string().min(1).max(255),
    companyName: z.string().max(255).optional(),
    /**
     * 🔴 GDPR — ez ADATMODELL, nem feature. A marketing-hozzájárulás a beküldés
     * pillanatában dől el; ha utólag vezetjük be, MINDEN meglévő címzettet újra kell
     * engedélyeztetni. Hiányában az ERP `false`-ként kezeli (fail-closed).
     */
    marketingConsent: z.boolean().default(false),
  })
  .refine(c => Boolean(c.email ?? c.phone), {
    message: 'e-mail vagy telefon közül legalább az egyik kötelező (személy-kulcs)',
  });
export type InboundCustomer = z.infer<typeof InboundCustomerSchema>;

/**
 * A közös boríték. Minden bejövő igény ezt viseli.
 *
 * ⛔ `tenantId` SZÁNDÉKOSAN NINCS BENNE (🔵-döntés, 2026-09-10 tty · `Am.3` `E3`): a tenantot
 * az INTEGRÁCIÓS KULCS adja, a payload soha. Egy igazság van, és az a kulcs.
 * 📌 A séma nem `.strict()` ⇒ aki mégis küld `tenantId`-t, annak a mezője **csendben
 * eldobódik** (Zod strip) — nem 400. Ez szándékos: a mező nem válhat rejtett függőséggé.
 * ⚠️ A dedup-tábla `tenant_id` OSZLOPA ettől FÜGGETLENÜL megmarad — az az RLS hordozója.
 *    A kettő neve hasonlít, a szerepe nem: boríték-MEZŐ ≠ tábla-OSZLOP.
 */
export const InboundRequestEnvelopeSchema = z.object({
  /** A BEKÜLDŐ által generált azonosító — az ERP-oldali dedup elsődleges kulcsa. */
  requestId: z.string().uuid(),
  schemaVersion: z.string().min(1),
  request: InboundRequestNameSchema,
  /** A beküldő saját kulcsa a saját során (a web üzleti kulcsa, nem uuid). */
  externalId: z.string().min(1).max(128),
  /** Melyik rendszer küldte. Naplózásra és hibakeresésre; jogosultságot NEM ad. */
  source: z.string().min(1).max(64),
  /** Amikor a felhasznalo BEKULDTE — uzleti idobelyeg (`E4` rokona). */
  submittedAt: TimestampSchema,
  /**
   * Az ELSO kezbesitesi kiserlet ideje (`Am.3` `E2/a`) — a RETRY-nal is VALTOZATLAN.
   *
   * ⛔ NEM azonos a `submittedAt`-tal: az azt mondja meg, mikor kattintott a vevo, ez azt,
   * mikor probaltuk eloszor atadni. A ketto osszemosasa pontosan az `E4` nema hibaja.
   *
   * 🔑 A fogado ezen zar: `firstAttemptAt < now − R` -> `410 BRIDGE_EVENT_STALE`. Ezert kell
   * a BORITEKBAN lennie (az alairt anyag resze), nem fejlecben — kulonben hamisithato.
   */
  firstAttemptAt: TimestampSchema,
  customer: InboundCustomerSchema,
});
export type InboundRequestEnvelope = z.infer<typeof InboundRequestEnvelopeSchema>;

/** Kapcsolatfelvétel / általános lead. */
export const ContactSubmittedPayloadSchema = z.object({
  subject: z.string().min(1).max(255),
  message: z.string().min(1).max(4000),
});

/** Szerviz-KÉRELEM. ⛔ Nem munkalap: azt az ERP nyitja, ha elfogadja. */
export const ServiceRequestedPayloadSchema = z.object({
  machineLabel: z.string().min(1).max(255),
  /** Az ERP cikk-azonosítója, HA a beküldő ismeri (katalógus-szinkronból). */
  productId: z.string().uuid().optional(),
  problemDescription: z.string().min(1).max(4000),
  preferredDate: TimestampSchema.optional(),
});

/** Alkatrész-érdeklődés. */
export const PartsInquiryPayloadSchema = z.object({
  machineLabel: z.string().min(1).max(255),
  items: z
    .array(
      z.object({
        description: z.string().min(1).max(255),
        quantity: z.number().int().positive().max(9999),
      })
    )
    .min(1)
    .max(50),
});

/**
 * Bérlés-IGÉNY. ⛔ **Nem foglalás.** Az elérhetőséget, az árat és a kauciót az ERP
 * dönti el; a válasz `rental.created` vagy elutasítás.
 */
export const BookingRequestedPayloadSchema = z.object({
  items: z
    .array(
      z.object({
        /**
         * 🔴 **`equipmentId`, NEM `productId`** — a bérlés-igény GÉP-PÉLDÁNYRA szól.
         *
         * Ez a mező korábban `productId` néven állt, *„az ERP cikk-azonosítója"*
         * magyarázattal. **Mérve téves** (2026-09-10, dev4-live): a web a
         * `GET portal/equipment` válaszából dolgozik, az pedig a
         * `RentalEquipment.id`-t adja `id` néven — nem a `Product.id`-t.
         *
         * ```text
         * products          264 sor
         * rental_equipment   38 sor
         * atfedes (r.id = p.id)   0     <- a ket id-ter DISZJUNKT
         * ```
         *
         * ⇒ a `RentalEquipment.id` egy `productId` nevű mezőben **soha nem oldódott
         * volna fel**, és mivel a mező opcionális volt, a fogadó **némán** a `label`-re
         * esett volna vissza. Ugyanaz az osztály, mint az `externalId` három jelentése:
         * egy név, két tábla.
         *
         * ⚠️ A `Product` szintjén nincs is mit foglalni: a `BookingItem` FK-ja
         * `RentalEquipment`-re mutat, és a napidíj is a **példányon** él.
         *
         * 🔑 **És KÖTELEZŐ, nem opcionális** (`1.1`-től). Opcionálisként a `label`-re
         * visszaeső ág **soha nem tudott volna `BookingItem`-et létrehozni** (a FK
         * kötelező) ⇒ a tétel némán kimaradt volna a foglalásból. A kötelezővé tétel
         * ezt `400`-zá teszi: a küldő MEGTUDJA, hogy nem történt meg (`E7`/`E8`).
         */
        equipmentId: z.string().uuid(),
        /** Ember-olvasható megnevezés — naplóra és admin-nézetre. NEM azonosít. */
        label: z.string().min(1).max(255),
        quantity: z.number().int().positive().max(999),
      })
    )
    .min(1)
    .max(100),
  /**
   * A vevo altal VALASZTOTT tartozekok. OPCIONALIS, es a hianya JELENTES:
   *
   * ```text
   * accessories megadva   ->  a vevo SZANDEKA          (accessorySource = customer)
   * hianyzik / ures       ->  az ERP a kuralt katalogusbol old fel  (erp-default)
   * ```
   *
   * 🔴 **KULON MEZO, es NEM az `items[]`-ben — ket kulon NEVTER** (🧭 rulingja, 2026-09-10):
   *
   * ```text
   * items[].equipmentId    RentalEquipment.id   PELDANY-szint
   * accessories[].productId  Product.id         CIKK-szint  (a #1997 accessories[].id-ja)
   * dev4-live atfedes:  rental_equipment.id ∩ products.id  ->  0
   * ```
   *
   * ⛔ Egy tomb NEM kulcsolhat ket tablat. Ez az osztaly ebben a korben HAROMSZOR jott elo
   * (`externalId` · `productId` a foglalas-tetelen · most a tartozek) — a gyoker nem a NEV,
   * hanem a kevert nevter; ezert zarja KET mezo.
   *
   * ⛔ **`kind` / ar / megnevezes SZANDEKOSAN NINCS a boritekban** (🧭 rulingja): a hid
   * **kulcsot es mennyiseget** visz, OSZTALYOZAST nem. A `kind` az ERP osztalyozasa (a gazda
   * mezoje), a web `kind`-je a web UI-dontese — ket kulon fogalom-ter. Merve, mi tortenik, ha
   * egy projekcio a gazda mezojet visszakuldi: a web `consumable` (uzemanyag, damil) es az
   * ERP `CONSIGNMENT` (bizomany, elszamolos) **HAMIS BARAT** — hasonloan hangzik, mas fogalom.
   * ⇒ igy a lekepezesi tabla **nem letezik, tehat nem is romolhat el**.
   *
   * ⚠️ A mezo ADDITIV: a web akkor kezd kuldeni, amikor tud — a szerzodes nem var ra.
   */
  accessories: z
    .array(
      z.object({
        /** Az ERP tartozek-CIKK azonositoja. ⛔ NEM `sku` (az a gyartoi cikkszam), es NEM `equipmentId`. */
        productId: z.string().uuid(),
        /**
         * ⛔ A nev `quantity`, NEM `qty` — 🧭 rulingja `qty`-t irt, es ez az EGYETLEN pont,
         * ahol elterek tole. Indok: ebben a fajlban a `quantity` mar `4` helyen all
         * (`items` · `parts.items` · `order.items` · itt); a `qty` bevezetese EGY fogalomra
         * KET nevet adna UGYANABBAN a semaban — pont az az osztaly, amit ma egesz nap zarunk
         * (`externalId` · `productId` · a tartozek-nevter). Az ALAK a rulingnak megfelel.
         *
         * A sapka `99`: 🌈 merese szerint a web `Math.max(1, Math.min(99, …))`-cel sapkaz,
         * es 🧭 `1..99`-et rulingolt. ⚠️ Az ERP-oldali darabszam-korlat NEM ebbol jon —
         * azt a keszlet donti el a jovahagyaskor.
         */
        quantity: z.number().int().positive().max(99),
      })
    )
    .max(100)
    .optional(),
  startDate: TimestampSchema,
  endDate: TimestampSchema,
  note: z.string().max(2000).optional(),
});

/**
 * Webshop kosár-IGÉNY. ⛔ **Nincs benne ár és nincs benne fizetési állapot** — az
 * elszámolás az ERP-é (`D5`). A beküldő azt mondja meg, mit kérnek, nem azt, mennyiért.
 */
export const OrderPlacedPayloadSchema = z.object({
  items: z
    .array(
      z.object({
        productId: z.string().uuid().optional(),
        sku: z.string().min(1).max(64),
        quantity: z.number().int().positive().max(9999),
      })
    )
    .min(1)
    .max(200),
  shippingAddress: z
    .object({
      postalCode: z.string().min(1).max(16),
      city: z.string().min(1).max(128),
      streetAddress: z.string().min(1).max(255),
      countryCode: z.string().length(2),
    })
    .optional(),
  note: z.string().max(2000).optional(),
});

/**
 * A teljes bejövő igény — boríték + a névhez tartozó payload.
 * A diszkrimináció a `request` mezőn megy.
 */
export const InboundRequestSchema = z.discriminatedUnion('request', [
  InboundRequestEnvelopeSchema.extend({
    request: z.literal('contact.submitted'),
    payload: ContactSubmittedPayloadSchema,
  }),
  InboundRequestEnvelopeSchema.extend({
    request: z.literal('service.requested'),
    payload: ServiceRequestedPayloadSchema,
  }),
  InboundRequestEnvelopeSchema.extend({
    request: z.literal('parts.inquiry_submitted'),
    payload: PartsInquiryPayloadSchema,
  }),
  InboundRequestEnvelopeSchema.extend({
    request: z.literal('booking.requested'),
    payload: BookingRequestedPayloadSchema,
  }),
  InboundRequestEnvelopeSchema.extend({
    request: z.literal('order.placed'),
    payload: OrderPlacedPayloadSchema,
  }),
]);
export type InboundRequest = z.infer<typeof InboundRequestSchema>;

/**
 * A nyugta, amit az ERP a beküldésre ad.
 *
 * ⚠️ Az `ACCEPTED` **átvételt** jelent, nem jóváhagyást: az érdemi döntés (elérhetőség,
 * ár, kaució) az ERP-ben születik, és eseményként megy vissza. A `DUPLICATE` a
 * `requestId` szerinti idempotens ismétlés — NEM hiba.
 */
export const InboundAckSchema = z.object({
  requestId: z.string().uuid(),
  status: z.enum(['ACCEPTED', 'DUPLICATE', 'REJECTED']),
  /** Az ERP-oldali azonosító, ha keletkezett tétel. */
  erpReference: z.string().uuid().optional(),
  /** Csak `REJECTED` esetén; ember által olvasható, gépi döntésre nem alkalmas. */
  reason: z.string().max(500).optional(),
  receivedAt: TimestampSchema,
});
export type InboundAck = z.infer<typeof InboundAckSchema>;
